import { gsap, ScrollTrigger, SplitText } from "../shared/globals.js";

const DEFAULT_START = "top 85%";
const TEXT_CHILD_SELECTOR = "p, li, blockquote, h1, h2, h3, h4, h5, h6";

export function createHeadingAnimationFeature({ prefersReducedMotion }) {
    let revealInitialized = false;
    const splitInstances = [];
    const revealTweens = [];

    function getNumberAttr(element, name, fallback) {
        const value = element.getAttribute(name);
        if (value === null || value === "") {
            return fallback;
        }

        const parsed = Number.parseFloat(value);
        return Number.isFinite(parsed) ? parsed : fallback;
    }

    function getRevealOptions(element, variant) {
        const baseOptions = {
            start: element.getAttribute("data-reveal-start") || DEFAULT_START,
            duration: getNumberAttr(element, "data-reveal-duration", variant === "lines" ? 0.8 : 0.7),
            delay: getNumberAttr(element, "data-reveal-delay", 0),
            stagger: getNumberAttr(element, "data-reveal-stagger", variant === "lines" ? 0.15 : 0.12),
            distance: getNumberAttr(element, "data-reveal-distance", variant === "lines" ? 60 : 32),
        };

        if (variant === "block") {
            baseOptions.scale = getNumberAttr(element, "data-reveal-scale", 0.98);
        }

        return baseOptions;
    }

    function createScrollTriggerConfig(element, options) {
        return {
            trigger: element,
            start: options.start,
            toggleActions: "play none none none",
            once: true,
        };
    }

    function registerTween(tween) {
        if (tween) {
            revealTweens.push(tween);
        }
    }

    function getTextTargets(element) {
        const customSelector = element.getAttribute("data-reveal-children");
        if (customSelector) {
            const customTargets = Array.from(element.querySelectorAll(customSelector));
            if (customTargets.length) {
                return customTargets;
            }
        }

        const directChildren = Array.from(element.children).filter((child) => child.matches(TEXT_CHILD_SELECTOR));
        if (directChildren.length) {
            return directChildren;
        }

        return [element];
    }

    function initLineReveal(element) {
        if (!SplitText) {
            return;
        }

        const options = getRevealOptions(element, "lines");
        const splitText = new SplitText(element, {
            type: "lines",
            linesClass: "heading-line",
        });

        splitInstances.push(splitText);

        splitText.lines.forEach((line) => {
            const wrapper = document.createElement("div");
            wrapper.style.overflow = "hidden";
            wrapper.style.display = "block";
            line.parentNode.insertBefore(wrapper, line);
            wrapper.appendChild(line);
        });

        gsap.set(splitText.lines, {
            y: options.distance,
            opacity: 0,
        });

        element.classList.add("split-initialized");

        registerTween(gsap.to(splitText.lines, {
            y: 0,
            opacity: 1,
            duration: options.duration,
            delay: options.delay,
            stagger: options.stagger,
            ease: "power3.out",
            clearProps: "transform,opacity",
            scrollTrigger: createScrollTriggerConfig(element, options),
        }));
    }

    function initTextReveal(element) {
        const options = getRevealOptions(element, "text");
        const targets = getTextTargets(element);

        if (!targets.length) {
            return;
        }

        gsap.set(targets, {
            y: options.distance,
            opacity: 0,
        });

        registerTween(gsap.to(targets, {
            y: 0,
            opacity: 1,
            duration: options.duration,
            delay: options.delay,
            stagger: options.stagger,
            ease: "power3.out",
            clearProps: "transform,opacity",
            scrollTrigger: createScrollTriggerConfig(element, options),
        }));
    }

    function initBlockReveal(element) {
        const options = getRevealOptions(element, "block");

        gsap.set(element, {
            y: options.distance,
            opacity: 0,
            scale: options.scale,
            transformOrigin: "50% 50%",
        });

        registerTween(gsap.to(element, {
            y: 0,
            opacity: 1,
            scale: 1,
            duration: options.duration,
            delay: options.delay,
            ease: "power3.out",
            clearProps: "transform,opacity",
            scrollTrigger: createScrollTriggerConfig(element, options),
        }));
    }

    function initHeadingAnimation() {
        if (revealInitialized) {
            return;
        }

        revealInitialized = true;

        const revealElements = Array.from(document.querySelectorAll("[data-reveal]"));
        if (!revealElements.length) {
            return;
        }

        if (prefersReducedMotion) {
            revealElements.forEach((element) => {
                element.classList.add("split-initialized");
            });
            return;
        }

        revealElements.forEach((element) => {
            const variant = element.getAttribute("data-reveal");

            if (variant === "lines") {
                initLineReveal(element);
                return;
            }

            if (variant === "text") {
                initTextReveal(element);
                return;
            }

            if (variant === "block") {
                initBlockReveal(element);
            }
        });

        ScrollTrigger.refresh();
    }

    function destroy() {
        revealTweens.forEach((tween) => {
            tween.scrollTrigger?.kill();
            tween.kill();
        });

        splitInstances.forEach((instance) => {
            instance.revert();
        });

        revealTweens.length = 0;
        splitInstances.length = 0;
        revealInitialized = false;
    }

    return {
        init: initHeadingAnimation,
        initHeadingAnimation,
        destroy,
    };
}
